import React, { useEffect, useState } from "react";
import api, { API_BASE, getToken } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Users, Plane, Package, Flag, LifeBuoy, ShieldCheck, Loader2, Activity, ShieldAlert,
} from "lucide-react";

const STAT_CARDS = [
  { key: "users", label: "Users", icon: Users },
  { key: "trips", label: "Trips", icon: Plane },
  { key: "deliveries", label: "Deliveries", icon: Package },
  { key: "active_deliveries", label: "Active", icon: Activity },
  { key: "connections", label: "Connections", icon: ShieldCheck },
  { key: "reports", label: "Open Reports", icon: Flag },
  { key: "support", label: "Support", icon: LifeBuoy },
  { key: "pending_verifications", label: "Pending IDs", icon: ShieldAlert },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ users: [], trips: [], deliveries: [], reports: [], requests: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, u, t, d, r, sup] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/trips"),
        api.get("/admin/deliveries"), api.get("/admin/reports"), api.get("/admin/support"),
      ]);
      setStats(s.data);
      setData({ users: u.data.users, trips: t.data.trips, deliveries: d.data.deliveries, reports: r.data.reports, requests: sup.data.requests });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setVerification = async (uid, status) => {
    try { await api.patch(`/admin/verifications/${uid}`, { status }); toast.success(`Verification set to ${status}`); load(); }
    catch { toast.error("Failed to update"); }
  };

  const govIdUrl = (fid) => fid ? `${API_BASE}/files/${fid}?auth=${encodeURIComponent(getToken() || "")}` : null;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>;

  return (
    <div>
      <PageHeader title="Admin Dashboard" subtitle="Platform overview and moderation tools." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
        {STAT_CARDS.map((c) => (
          <div key={c.key} className="rounded-2xl border border-slate-200 bg-white p-4">
            <c.icon className="w-5 h-5 text-primary" />
            <p className="text-2xl font-bold mt-2">{stats?.[c.key] ?? 0}</p>
            <p className="text-xs text-slate-400">{c.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="users">
        <TabsList className="rounded-xl mb-5 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-lg">Users</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="admin-tab-verifications" className="rounded-lg">Verifications</TabsTrigger>
          <TabsTrigger value="trips" data-testid="admin-tab-trips" className="rounded-lg">Trips</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="admin-tab-deliveries" className="rounded-lg">Deliveries</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports" className="rounded-lg">Reports</TabsTrigger>
          <TabsTrigger value="support" data-testid="admin-tab-support" className="rounded-lg">Support</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Verification</TableHead></TableRow></TableHeader>
              <TableBody>{data.users.map((u) => (
                <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-slate-500">{u.email}</TableCell>
                  <TableCell><span className={u.role === "admin" ? "text-primary font-semibold" : ""}>{u.role}</span></TableCell>
                  <TableCell>{u.status || "active"}</TableCell>
                  <TableCell className="capitalize">{u.verification_status}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="verifications">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Government ID</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>{data.users.filter((u) => u.verification_status === "pending").map((u) => (
                <TableRow key={u.id} data-testid={`admin-verify-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-slate-500">{u.email}</TableCell>
                  <TableCell>
                    {u.gov_id_file_id ? (
                      <a href={govIdUrl(u.gov_id_file_id)} target="_blank" rel="noopener noreferrer" data-testid={`admin-gov-id-${u.id}`}>
                        <img src={govIdUrl(u.gov_id_file_id)} alt="Government ID" className="w-24 h-16 object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-primary transition" />
                      </a>
                    ) : <span className="text-xs text-slate-400">No document</span>}
                  </TableCell>
                  <TableCell className="capitalize">{u.verification_status}</TableCell>
                  <TableCell className="flex gap-2">
                    <Button size="sm" onClick={() => setVerification(u.id, "verified")} data-testid={`verify-approve-${u.id}`} className="rounded-lg">Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => setVerification(u.id, "rejected")} data-testid={`verify-reject-${u.id}`} className="rounded-lg">Reject</Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
            {data.users.filter((u) => u.verification_status === "pending").length === 0 && <p className="text-center text-sm text-slate-400 py-8">No pending verifications.</p>}
          </div>
        </TabsContent>

        <TabsContent value="trips">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Traveller</TableHead><TableHead>Route</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{data.trips.map((t) => (
                <TableRow key={t.id}><TableCell className="font-mono text-xs">{t.id}</TableCell><TableCell>{t.traveller_name}</TableCell><TableCell>{t.origin} → {t.destination}</TableCell><TableCell>{t.travel_date}</TableCell><TableCell><StatusBadge status={t.status} /></TableCell></TableRow>
              ))}</TableBody>
            </Table>
            {data.trips.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No trips yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="deliveries">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Sender</TableHead><TableHead>Route</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{data.deliveries.map((d) => (
                <TableRow key={d.id}><TableCell className="font-mono text-xs">{d.id}</TableCell><TableCell>{d.sender_name}</TableCell><TableCell>{d.pickup} → {d.destination}</TableCell><TableCell>{d.category}</TableCell><TableCell><StatusBadge status={d.status} /></TableCell></TableRow>
              ))}</TableBody>
            </Table>
            {data.deliveries.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No deliveries yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="reports">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Target</TableHead><TableHead>Reason</TableHead><TableHead>Description</TableHead></TableRow></TableHeader>
              <TableBody>{data.reports.map((r) => (
                <TableRow key={r.id}><TableCell className="capitalize">{r.target_type}</TableCell><TableCell className="font-mono text-xs">{r.target_id}</TableCell><TableCell>{r.reason}</TableCell><TableCell className="text-slate-500">{r.description || "—"}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
            {data.reports.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No reports.</p>}
          </div>
        </TabsContent>

        <TabsContent value="support">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Category</TableHead><TableHead>Subject</TableHead><TableHead>Message</TableHead></TableRow></TableHeader>
              <TableBody>{data.requests.map((r) => (
                <TableRow key={r.id}><TableCell>{r.user_email}</TableCell><TableCell>{r.category}</TableCell><TableCell>{r.subject}</TableCell><TableCell className="text-slate-500 max-w-xs truncate">{r.message}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
            {data.requests.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No support requests.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
